import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import EnrolmentLayout from '../../../../../common/hub/EnrolmentLayout';
import { OptionCard, OptionCardContent } from '../../../../../components/EnrolmentManagement/OptionCard';
import { IntentIntro } from '../../../../../components/IntentIntro';
import { useEnrolmentDetailQuery } from '../../../../../hooks/queries/useEnrolmentQuery';
import { getAccountDetails, getSessionReportId } from '../../../../../selectors/hubSelector';
import { AccountDetails } from '../../../../../types/hubInterfaces';
import { getNAPLANInfo } from '../../../../../util/helper/helperNAPLAN';
import { isGroupSessionCheck } from '../../../../../util/helpers';
import { PATH_HUB_HOME } from '../../../../../util/pagePath';
import { usePageHeader } from '../../../../../v2/app-shell/usePageHeader';
import { generateLearningAdjustmentsOptions } from './generateLearningAdjustmentsOptions';

const SUBJECTS_WITH_PRACTICE = ['Mathematics', 'Biology', 'Chemistry', 'Physics'];
const ONE_ON_ONE = 'Course';

export const ManageServiceLearningAdjustments = () => {
  const { enrolmentId } = useParams<{ enrolmentId: string }>();
  const { data: enrolmentDetail, isLoading, error } = useEnrolmentDetailQuery({ enrolmentId });
  const history = useHistory();
  const { setHeader } = usePageHeader();
  if (!enrolmentId) {
    history.push(PATH_HUB_HOME);
  }

  useEffect(() => {
    setHeader({
      label: 'Learning adjustments',
      showIcon: false,
      showBackButton: true,
    });
  }, [setHeader]);

  const {
    studentName,
    customerNumber: studentCustomerNumber,
    enrolmentDiscipline,
    yearAsInteger: yearLevel,
    enrolmentSubject,
    packageMasterCode,
    enrolmentType: courseType,
  } = enrolmentDetail ?? {};
  const { customerNumber }: AccountDetails = useSelector(getAccountDetails);
  const sessionReportToken = useSelector((state) => getSessionReportId(state, enrolmentId));
  const { showNAPLAN } = getNAPLANInfo({ enrolmentSubject, packageMasterCode, courseType, checkDate: false });
  const showPractice = courseType === ONE_ON_ONE && SUBJECTS_WITH_PRACTICE.includes(enrolmentSubject);
  const isAccountOwner = customerNumber === studentCustomerNumber;
  const isGroup = isGroupSessionCheck(courseType);

  const options = generateLearningAdjustmentsOptions({
    isAccountOwner,
    studentName,
    sessionReportToken,
    enrolmentId,
    isGroup,
    showNAPLAN,
    showPractice,
  });

  return (
    <EnrolmentLayout
      isLoading={isLoading}
      error={error}
      sectionHeading={`${studentName}'s ${enrolmentDiscipline}`}
      mainHeading={`Year ${yearLevel} ${enrolmentDiscipline}`}
      enrolmentId={enrolmentId}
      showEnrolmentStatus={false}
      showSummerModeEnrolmentStatus={false}
    >
      <div className="flex min-h-screen flex-col px-5 pb-10 md:px-10">
        <div className="container mr-auto flex flex-col gap-y-5 px-0 pb-16 md:gap-y-10">
          <IntentIntro>How can we better support in-session learning?</IntentIntro>
          <ul className="list-none">
            {options.map(({ label, linkTo, type, headerLabel }) => {
              const [pathname, search] = linkTo.split('?');
              const linkToWithState = headerLabel
                ? { pathname, search: search ? `?${search}` : '', state: { headerLabel } }
                : linkTo;
              return (
                <li key={label}>
                  <OptionCard linkTo={linkToWithState} type={type}>
                    <OptionCardContent>{label}</OptionCardContent>
                  </OptionCard>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </EnrolmentLayout>
  );
};
